import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import Store from '../../../state/Store';
import MultiChartExportProvider from '@hooks/export/utilities/MultiChartExportProvider';
import { ProductsMarketRetailerLineChartInsight } from './Model';
import { PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { CollectionInsightType } from '@components/collections/Model';
import ProductsMarketCard, {
  ProductsMarketCardProps,
} from '@pages/product-analyzer/trends/products-market-card/ProductsMarketCard';
import { getProductsTrends } from '@services/productTab/ProductsTrends.service';
import ProductsRetailerCard, {
  ProductsRetailerCardProps,
} from '@pages/product-analyzer/trends/products-retailer-card/ProductsRetailerCard';
import {
  processProductsMarketLineChartData,
  processProductsRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { useLineChartInsight } from '@utils/collectionUtils/hooks/UseLineChartInsight';

enableChartsExporting();

type ProductsMarketRetailerLineChartInsightRendererType = InsightRendererProps<ProductsMarketRetailerLineChartInsight>;

const ProductsMarketRetailerLineChartInsightContainer: FC<ProductsMarketRetailerLineChartInsightRendererType> = ({
  insight,
  onLoaded,
  onExportReady,
}) => {
  const { apiParams, selectedLineChartSaleType, selectedRetailerKpiOption, currencySymbol } = insight.data;

  const { isLoading, chartData, getWarningBannerWrapper } = useLineChartInsight({
    apiParams,
    dataType: insight.dataType,
    period: apiParams.period,
    pageType: PageType.RI_PRODUCT,
    marketChartKey: 'MARKET',
    retailerChartKey: 'RETAILER',
    collectionInsightType: CollectionInsightType.ProductTrendsMarketRetailerLineChart,
    onLoaded,
    onExportReady,
    apiRequest: async () => {
      return getProductsTrends({
        ...apiParams.productsParam,
      });
    },
    processMarketData: (data) => processProductsMarketLineChartData(data, selectedLineChartSaleType),
    processRetailerData: (data) =>
      processProductsRetailerLineChartData(data, selectedLineChartSaleType, selectedRetailerKpiOption),
  });

  const productCardsProps = {
    trendsKpi: chartData!,
    selectedPeriodicity: apiParams.periodType,
    selectedLineChartSaleType: selectedLineChartSaleType,
    factRevenue: { currencySymbol: currencySymbol, currencyLabel: '' },
    isLoading: false,
    pageTabName: PageName.RI_PRODUCT_TRENDS,
    subSectionForAmplitudeTracking: SubSection.PRODUCT_TRENDS,
  };

  const marketCardProps: ProductsMarketCardProps = {
    ...productCardsProps,
    isPeriodYTDorMAT: false,
  };

  const retailCardProps: ProductsRetailerCardProps = {
    ...productCardsProps,
    selectedOption: selectedRetailerKpiOption,
  };

  if (isLoading) {
    return renderLoading();
  }

  return getWarningBannerWrapper(
    <>
      <ProductsMarketCard {...marketCardProps} />
      <ProductsRetailerCard {...retailCardProps} />
    </>,
  );
};

export const ProductsMarketRetailerLineChartInsightRenderer: FC<ProductsMarketRetailerLineChartInsightRendererType> = (
  props,
) => {
  return (
    <Provider store={Store}>
      <MultiChartExportProvider>
        <ProductsMarketRetailerLineChartInsightContainer {...props} />
      </MultiChartExportProvider>
    </Provider>
  );
};
